import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Table, Column, AutoSizer, SortDirection } from 'react-virtualized';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { Chart as ChartJS, registerables } from 'chart.js';
import Title from '../../../sharedComponents/Title/Title';
import useDataSuite from '../useDataSuite';
import { FaClipboardList, FaTrash } from 'react-icons/fa';
import { BsPencilSquare } from 'react-icons/bs';
import formatters from '../../../config/formatters';
import { IoMdInformationCircle } from "react-icons/io";
import { Tooltip } from "@mui/material";
import useDataSuiteWidgets from './useDataSuiteWidgets';
import { isEqual } from "lodash";
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import EmptySearchResultsMessage from '../../../sharedComponents/ErrorMessages/EmptySearchResultsMessage';
import { Theme } from '../../../config/constants';
import { sortableHeaderRenderer } from '../../../sharedComponents/Tables/SortableHeaderRenderer';

ChartJS.register(...registerables);

const LeakageWidget = (props) => {
    const navigate = useNavigate();
    const { w, i } = props;
    const { onClickWidgetDrawerOpen, onClickDeleteWidget } = useDataSuite.useWidgetControls();
    const { widgetTitle, leakageTableData, filtersEnabled } = useDataSuiteWidgets.useLeakageWidget();
    const widgetOptionsOpen = useSelector(({ui}) => ui.slideDrawer);
    const selectedFilters = useSelector(({datasuite}) => datasuite.selectedFilters);
    const selectedDetailsFilters = useSelector(({datasuite}) => datasuite.selectedDetailsFilters);
    const dateString = useSelector(({dates}) => dates.datasuiteDateString);
    const detailsDateString = useSelector(({dates}) => dates.datasuiteDetailsDateString);
    const isLoading = useSelector(({datasuite}) => datasuite.isLoading.leakageLoading);
    const { getLeakage } = useDataSuite.useDataSuite();
    const { numberFormatter } = formatters();
    const [isWidgetHovered, setIsWidgetHovered] = useState(false);
    const [sortBy, setSortBy] = useState('');
    const [sortDirection, setSortDirection] = useState(null);
    const selectedManufacturers = useSelector(({user}) => user.selectedManufacturers);

    const prevFilters = useRef(null);
    const prevDate = useRef(null);
    const prevManufacturers = useRef(null);

    const handleMouseEnter = () => {
        setIsWidgetHovered(true);
    };
    const handleMouseLeave = () => {
        setIsWidgetHovered(false);
    };

    const onClickSeeDetails = () => {
        navigate({pathname: '/salesstream/details', search: "?Leakage"});
    };

    useEffect(() => {
        if (isEqual(selectedFilters, prevFilters.current) &&
            isEqual(dateString, prevDate.current) &&
            isEqual(selectedManufacturers, prevManufacturers.current)
        ) {
            getLeakage(true);
        } else {
            getLeakage(false);
        }
        prevFilters.current = selectedFilters;
        prevDate.current = dateString;
        prevManufacturers.current = selectedManufacturers;

    // eslint-disable-next-line
    }, [selectedFilters, selectedDetailsFilters, dateString, detailsDateString, selectedManufacturers]);

    const sortedLeakageTableData = (() => {
        if (!leakageTableData) return [];
        const sorted = [...leakageTableData];
        if (sortBy === 'total_cases_comp') {
            sorted.sort((a, b) => {
                if (sortDirection === SortDirection.ASC) {
                    return (a.total_cases_comp || 0) - (b.total_cases_comp || 0);
                } else {
                    return (b.total_cases_comp || 0) - (a.total_cases_comp || 0);
                }
            });
        }
        return sorted;
    })();

    return (
        <Col onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ position: 'relative'}} >
            <Row className='d-flex align-items-center'>
                <Col className='w-fc pr-0'>
                    <div className='d-flex align-items-center px-0'>
                    <Title title={widgetTitle}/>
                    <Tooltip title='Displays operator purchases from competing manufacturers in your categories. Operator is a current customer and you will be able to see your % of case qty compared to competitive MFRs. By default listed from highest % to lowest %. Helpful for identifying opportunities with direct partners who may be noncompliant.  Useful at the operator and location level.' arrow>
                        <div className='pt-3 pb-2'>
                        <IoMdInformationCircle className="text-size-18 " style={{color: Theme().colors.accent}}/>
                        </div>
                    </Tooltip>
                    </div>
                </Col>
                {(widgetOptionsOpen || isWidgetHovered) && ( <>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label replaceWidgetButton'
                            size='sm'
                            color='blueFilter'
                            onClick={() => onClickWidgetDrawerOpen("Replace Widget", true, i)}>
                            <BsPencilSquare color='white' />
                        </Button>
                    </Col>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block
                            className='py-1 button-label deleteWidgetButton'
                            size='sm'
                            color='redLight'
                            onClick={() => onClickDeleteWidget(w)}>
                            <FaTrash color='white' />
                        </Button>
                    </Col>
                </>
                )}
                <Col className={`w-fc me-3 ms-auto px-1 pt-3 pb-2`}>
                <Button
                    block
                    className={`py-1 button-label expandWidgetButton`}
                    size='sm'
                    outline={true}
                    color={Theme().colors.accentName}
                    onClick={() => onClickSeeDetails()}>
                    <FaClipboardList color={Theme().colors.accentName} style={{position: 'relative', bottom: '1px'}}/> See Details
                </Button>
                </Col>
            </Row>
            {isLoading &&
              <Row className="mx-auto" style={{minHeight: '315px'}}>
                  <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                      <Spinner style={{marginLeft:'50%'}} color='beplBlue' animation="border" />
                  </Col>
              </Row>
            }
            {(sortedLeakageTableData?.length <= 0) && !isLoading &&
            <Row className="mx-auto" style={{height: '70%'}}>
                <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                    {(!isLoading && (sortedLeakageTableData.length <= 0) && filtersEnabled) &&
                    <EmptySearchResultsMessage />
                    }
                    {(!isLoading && (sortedLeakageTableData.length <= 0) && !filtersEnabled) &&
                    <EmptyDataMessage />
                    }
                </Col>
            </Row>
            }
            {(sortedLeakageTableData?.length > 0) && !isLoading &&
            <Row className="w-100 mb-3 mx-0" style={{minHeight: '315px'}}>
                <Col className="col-12 text-size-14 p-0">
                    <Row style={{overflowY: 'auto', height: '100%'}}>
                        <AutoSizer>
                            {({width, height}) => (
                                <Table className="table leakageTable border-grayLight" id="leakageTable"
                                    width={width * 0.97}
                                    height={height}
                                    headerHeight={34.5}
                                    rowHeight={38}
                                    rowCount={sortedLeakageTableData.length}
                                    rowGetter={({ index }) => sortedLeakageTableData[index]}
                                    sort={({ sortBy: sb, sortDirection: sd }) => {
                                        setSortBy(sb);
                                        setSortDirection(sd);
                                    }}
                                    sortBy={sortBy}
                                    sortDirection={sortDirection}
                                >
                                    <Column
                                    label='Location Name'
                                    dataKey='location_name'
                                    width={width * 0.4}
                                    disableSort
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Category'
                                    dataKey='category'
                                    disableSort
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Your Cases'
                                    dataKey='total_cases'
                                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                    disableSort
                                    />
                                    <Column
                                    width={width * 0.2}
                                    label='Opportunity Cases'
                                    dataKey='total_cases_comp'
                                    cellRenderer={({cellData}) => numberFormatter.format(cellData)}
                                    headerRenderer={sortableHeaderRenderer}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Row>
                </Col>
            </Row>
            }
        </Col>
    );

}

export default LeakageWidget;
